// Friendly labels for the eligibility codes the campaign preview
// endpoint emits. Never render the raw enum to admins — the
// backend code is stable, but the UI copy should be humane.
//
// Codes come from Services/Communication/SuppressionService (the
// send-time decision) plus the preview endpoint's own additions
// ('invalid_contact' when normalization fails, 'transactional' for
// campaigns that bypass marketing suppression).

export type EligibilityCode =
  | "eligible"
  | "transactional"
  | "suppressed"
  | "consent_unknown"
  | "no_consent_on_file"
  | "consent_expired"
  | "invalid_address"
  | "invalid_contact"
  | "missing_contact"
  | string;

export function eligibilityLabel(code: EligibilityCode | null | undefined): string {
  switch ((code ?? "").toLowerCase()) {
    case "eligible":            return "Eligible";
    case "transactional":       return "Eligible — Transactional";
    case "suppressed":          return "Marketing opt-out";
    case "no_consent_on_file":
    case "consent_unknown":     return "Consent not on file";
    case "consent_expired":     return "Consent expired";
    case "invalid_address":
    case "invalid_contact":     return "Invalid contact";
    case "missing_contact":     return "Contact information missing";
    default:                    return code ? code : "—";
  }
}

/** True when the recipient will actually be sent to. */
export function isDeliverable(code: EligibilityCode | null | undefined): boolean {
  const c = (code ?? "").toLowerCase();
  return c === "eligible" || c === "transactional";
}

/** MUI-palette-compatible chip tone tuple. */
export function eligibilityChipTone(code: EligibilityCode | null | undefined): { bg: string; fg: string } {
  if (isDeliverable(code)) return { bg: "#DCFCE7", fg: "#15803D" };
  return { bg: "#FEE2E2", fg: "#B91C1C" };
}

/** Human label for the source_segment enum. */
export function sourceSegmentLabel(seg: string | null | undefined): string {
  switch ((seg ?? "").toLowerCase()) {
    case "students": return "Students";
    case "parents":  return "Parents";
    case "staff":    return "Staff";
    case "admins":   return "Admins";
    case "external": return "External";
    default:         return seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : "—";
  }
}
